import React, { useMemo } from 'react'
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material'
import { OpenInNew, PictureAsPdf } from '@mui/icons-material'
import { SummaryTable, SummaryTableRow } from 'components/SummaryTable'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { copyToClipboard } from 'shared/utils'
import { useGetTreeById } from 'shared/hooks/trees/hooks'
import { getNodeUrl } from 'components/utility/TreeNavigation'
import { createIdMap } from '../version_mapping/utility'
import { ExternalUrlsLeavesTable } from './ExternalUrlsLeavesTable'
import { ExternalUrlsNodesTable } from './ExternalUrlsNodesTable'
import { INode } from 'shared/types/tree'
import {
  PdfTreeTranslateDiscoveryJob,
  PdfTreeTranslateDiscoveryLeafMatch,
  PdfTreeTranslateDiscoveryNodeMatch,
} from './types'

function getSummaryRows(
  result: PdfTreeTranslateDiscoveryJob['data'],
): SummaryTableRow[] {
  return [
    { cells: ['Unique PDFs', result.total_unique_pdfs] },
    { cells: ['Node References', result.total_node_references] },
    { cells: ['Leaf References', result.total_leaf_references] },
  ]
}

type PdfUrlRow = {
  domain: string
  fileName: string
  url: string
  nodeId: string | undefined
}

function buildLeafToParentMap(node: INode): Map<string, string> {
  const map = new Map<string, string>()
  for (const leaf of node.leaves ?? []) {
    map.set(leaf.id, node.id)
  }
  for (const child of node.nodes ?? []) {
    for (const [leafId, parentId] of buildLeafToParentMap(child)) {
      map.set(leafId, parentId)
    }
  }
  return map
}

function buildUrlToNodeIdMap(
  nodeMatches: PdfTreeTranslateDiscoveryNodeMatch[],
  leafMatches: PdfTreeTranslateDiscoveryLeafMatch[],
  leafToParent: Map<string, string>,
): Map<string, string> {
  const map = new Map<string, string>()
  for (const match of nodeMatches) {
    for (const url of match.urls) {
      if (!map.has(url)) map.set(url, match.node_id)
    }
  }
  for (const match of leafMatches) {
    const parentNodeId = leafToParent.get(match.leaf_id)
    if (!map.has(match.url) && parentNodeId) {
      map.set(match.url, parentNodeId)
    }
  }
  return map
}

function getPdfUrlRows(
  urls: string[],
  urlToNodeId: Map<string, string>,
): PdfUrlRow[] {
  return urls.map((url) => {
    try {
      const parsedUrl = new URL(url)
      const pathnameParts = parsedUrl.pathname.split('/').filter(Boolean)
      const rawFileName = pathnameParts[pathnameParts.length - 1] ?? url

      return {
        url,
        domain: parsedUrl.hostname,
        fileName: decodeURIComponent(rawFileName),
        nodeId: urlToNodeId.get(url),
      }
    } catch {
      return {
        url,
        domain: 'Unknown',
        fileName: url,
        nodeId: urlToNodeId.get(url),
      }
    }
  })
}

function toExternalNodeEntry(match: PdfTreeTranslateDiscoveryNodeMatch) {
  return {
    node_id: match.node_id,
    node_type: match.node_type,
    node_title: match.node_title,
    field_path: match.field_path,
    match_result: match.urls,
    text_value: match.urls.join('\n'),
  }
}

function toExternalLeafEntry(match: PdfTreeTranslateDiscoveryLeafMatch) {
  return {
    leaf_id: match.leaf_id,
    leaf_type: match.leaf_type,
    field_path: match.field_path,
    match_result: [match.url],
    text_value: match.url,
  }
}

/**
 * Renders the results of a PDF discovery job.
 *
 * @param result - The PDF discovery job result
 * @returns Formatted display of discovered PDF URLs and their tree locations
 */
export function PdfTreeTranslateDiscoveryResponse({
  result,
}: {
  result: PdfTreeTranslateDiscoveryJob
}) {
  const { data, error, meta } = result
  const { tree } = useGetTreeById({
    treeId: meta.tree_id,
    versionId: meta.version_id,
  })

  const idMap = useMemo(() => {
    return tree ? createIdMap(tree) : undefined
  }, [tree])

  const leafToParent = useMemo(
    () => (tree ? buildLeafToParentMap(tree) : new Map<string, string>()),
    [tree],
  )
  const urlToNodeId = useMemo(
    () =>
      buildUrlToNodeIdMap(data.node_matches, data.leaf_matches, leafToParent),
    [data.node_matches, data.leaf_matches, leafToParent],
  )

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message)
        : JSON.stringify(error)

    return (
      <Box padding="10px">
        <Alert severity="error">
          <strong>ERROR:</strong> {errorMessage}
        </Alert>
      </Box>
    )
  }

  const nodeEntries = data.node_matches.map(toExternalNodeEntry)
  const leafEntries = data.leaf_matches.map(toExternalLeafEntry)
  const pdfUrlRows = getPdfUrlRows(data.unique_pdf_urls, urlToNodeId)
  const pdfUrlColumns: Column<PdfUrlRow>[] = [
    {
      title: 'File Name',
      rowContent: ({ fileName, url }) => (
        <Typography
          variant="body2"
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: 'break-word' }}
        >
          {fileName}
        </Typography>
      ),
      rowString: ({ url }) => url,
    },
    {
      title: 'Domain',
      rowContent: ({ domain }) => (
        <Typography variant="body2">{domain}</Typography>
      ),
      skipExport: true,
    },
    {
      title: 'Tree Location',
      rowContent: ({ nodeId, fileName }) => {
        if (!nodeId || !idMap?.[nodeId])
          return <Typography variant="body2">—</Typography>
        const node = idMap[nodeId].node
        const treeUrl = getNodeUrl(node, idMap)
        return (
          <Typography
            variant="body2"
            component="a"
            href={treeUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View ${fileName} in Tree`}
          >
            View in Tree
          </Typography>
        )
      },
      rowString: ({ nodeId }) => {
        if (!nodeId || !idMap?.[nodeId]) return ''
        const node = idMap[nodeId].node
        return getNodeUrl(node, idMap)
      },
    },
    {
      title: 'Copy URL',
      rowContent: ({ url }) => (
        <Button
          size="small"
          variant="text"
          onClick={() => copyToClipboard(url)}
        >
          Copy
        </Button>
      ),
      skipExport: true,
    },
  ]

  return (
    <Box>
      <Box padding="10px">
        <Typography variant="h6" gutterBottom>
          PDF Discovery Summary
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={2}>
          <Chip
            icon={<PictureAsPdf />}
            label={`${data.total_unique_pdfs} Unique PDFs`}
            color="primary"
            size="small"
          />
          <Chip
            icon={<OpenInNew />}
            label={`${data.total_node_references} Node References`}
            color="default"
            size="small"
          />
          <Chip
            icon={<OpenInNew />}
            label={`${data.total_leaf_references} Leaf References`}
            color="default"
            size="small"
          />
        </Stack>
        <SummaryTable
          rows={getSummaryRows(data)}
          firstColumnSx={{ fontStyle: 'italic' }}
        />
      </Box>

      {pdfUrlRows.length > 0 && (
        <Box padding="10px">
          <Typography variant="subtitle1" gutterBottom>
            Unique PDF URLs ({pdfUrlRows.length})
          </Typography>
          <Table
            title="PDF URL Table"
            items={pdfUrlRows}
            columns={pdfUrlColumns}
            overrideHeight={220}
            canExport
          />
        </Box>
      )}

      <ExternalUrlsNodesTable
        externalUrls={nodeEntries}
        title="PDF Node References"
        idMap={idMap}
      />
      <ExternalUrlsLeavesTable
        externalUrls={leafEntries}
        title="PDF Leaf References"
        idMap={idMap}
      />

      {data.total_unique_pdfs === 0 && (
        <Box padding="10px">
          <Alert severity="info">No PDF URLs were found in this tree.</Alert>
        </Box>
      )}
    </Box>
  )
}
